// Port of api/fuel/app/classes/insights/game/peers.php
// JS note:
// - PHP can refresh missing cache rows via Sync\Type\Game\Insights::run().
// - Offline JS has no equivalent sync runner, so this port prefers cached `insight_game`
//   snapshots when present and fills gaps from live local tables.

import Segment from 'offline/insights/segment'
import Util from 'offline/util'
import Constants from 'offline/constants'
import DB from 'offline/db'

function parseInsightData(row) {
	if (!row || !row.insight_game_data) return {}
	try {
		return JSON.parse(row.insight_game_data) || {}
	} catch (e) {
		return {}
	}
}

function stddev(values) {
	if (!values.length) return 0
	var sum = 0
	for (var i = 0; i < values.length; i++) sum += values[i]
	var mean = sum / values.length
	var variance = 0
	for (var j = 0; j < values.length; j++) variance += Math.pow(values[j] - mean, 2)
	return Math.round(Math.sqrt(variance / values.length) * 100) / 100
}

function percentileFromBands(distribution, value, total) {
	if (value === null || !total) return null
	var below = 0
	for (var i = 1; i <= 10; i++) {
		if (i < Math.floor(value)) below += parseInt(distribution[i] || 0, 10)
	}
	return Math.round((below / total) * 100)
}

function bucketLabel(count) {
	if (count === 1) return '1'
	if (count === 2) return '2'
	if (count === 3) return '3'
	if (count <= 5) return '4-5'
	if (count <= 7) return '6-7'
	if (count <= 10) return '8-10'
	if (count <= 15) return '11-15'
	if (count <= 20) return '16-20'
	if (count <= 25) return '21-25'
	if (count <= 30) return '26-30'
	if (count <= 40) return '31-40'
	if (count <= 50) return '41-50'
	if (count <= 75) return '51-75'
	if (count <= 100) return '76-100'
	if (count <= 200) return '101-200'
	return '200+'
}

function weekKey(timestamp) {
	return Util.date('Y', timestamp) + '/' + Util.weekofyear(timestamp)
}

function parseHistoryRows(rows) {
	var history = []
	for (var hr = 0; hr < rows.length; hr++) {
		history.push({
			date: parseInt(rows[hr].insight_game_date, 10),
			owners: parseInt(rows[hr].insight_game_owners || 0, 10),
			prevowners: parseInt(rows[hr].insight_game_prevowners || 0, 10),
			wishlisted: parseInt(rows[hr].insight_game_wishlisted || 0, 10),
			raters: parseInt(rows[hr].insight_game_raters || 0, 10),
			players: parseInt(rows[hr].insight_game_players || 0, 10),
			totalPlays: parseInt(rows[hr].insight_game_total_plays || 0, 10),
			avgRating: parseFloat(rows[hr].insight_game_avg_rating || 0),
			avgPlays: parseFloat(rows[hr].insight_game_avg_plays || 0),
			retention: parseFloat(rows[hr].insight_game_retention || 0),
			rank: parseInt(rows[hr].insight_game_rank || 0, 10),
			weight: parseFloat(rows[hr].insight_game_weight || 0),
			bggRating: parseFloat(rows[hr].insight_game_bgg_rating || 0),
			ownersAdded: parseInt(rows[hr].insight_game_owners_added || 0, 10),
			ownersRemoved: parseInt(rows[hr].insight_game_owners_removed || 0, 10),
			wishAdded: parseInt(rows[hr].insight_game_wish_added || 0, 10),
			wishRemoved: parseInt(rows[hr].insight_game_wish_removed || 0, 10),
			wtpAdded: parseInt(rows[hr].insight_game_wtp_added || 0, 10),
			wtpRemoved: parseInt(rows[hr].insight_game_wtp_removed || 0, 10),
			playsMonth: parseInt(rows[hr].insight_game_plays_month || 0, 10),
			sessionsMonth: parseInt(rows[hr].insight_game_sessions_month || 0, 10),
			playersNew: parseInt(rows[hr].insight_game_players_new || 0, 10),
			hoursMonth: parseFloat(rows[hr].insight_game_hours_month || 0),
			ratingsAdded: parseInt(rows[hr].insight_game_ratings_added || 0, 10),
			ratingsChanged: parseInt(rows[hr].insight_game_ratings_changed || 0, 10),
			ratingsRemoved: parseInt(rows[hr].insight_game_ratings_removed || 0, 10)
		})
	}
	return history
}

export default Segment.extend({
	name: 'peers',
	priority: 5,
	requires: ['init'],
	types: [Constants.TYPE_USER],
	run: function(type, owner, userIndex, detail, filter, vars) {

		var userId = owner.get_id()
		var gameId = parseInt(vars.id, 10)
		var username = owner.get_name().toLowerCase()
		var userRating = this._data.has_rating ? parseFloat(this._data.rating.get_rating_value()) : null
		var userPlays = this._data.totalPlays || 0
		var playsRaw = this._data.plays_raw || []
		var playersRaw = this._data.players_raw || []

		var latestInsightRows = DB.query(
			'SELECT * FROM insight_game WHERE game_id = ' + gameId + ' ORDER BY insight_game_date DESC LIMIT 1'
		)
		var latestInsight = latestInsightRows.length ? latestInsightRows[0] : null
		var cachedData = parseInsightData(latestInsight)
		var historyRows = DB.query('SELECT * FROM insight_game WHERE game_id = ' + gameId + ' ORDER BY insight_game_date ASC')
		var history = parseHistoryRows(historyRows)
		var hasCachedCommunity = !!latestInsight && !!Object.keys(cachedData).length

		var ratingRows = Util.queryFast(
			'SELECT user_id, rating_value AS rating FROM rating WHERE game_id = ' + gameId + ' AND rating_value > 0'
		)
		var ratingMap = {}
		var ratingDistribution = cachedData.ratingDistribution || {}
		if (!Object.keys(ratingDistribution).length) {
			for (var rd = 1; rd <= 10; rd++) ratingDistribution[rd] = 0
			for (var rr = 0; rr < ratingRows.length; rr++) {
				var rounded = Math.floor(parseFloat(ratingRows[rr].rating))
				if (rounded >= 1 && rounded <= 10) ratingDistribution[rounded]++
				ratingMap[parseInt(ratingRows[rr].user_id, 10)] = parseFloat(ratingRows[rr].rating)
			}
		} else {
			for (var rm = 0; rm < ratingRows.length; rm++) {
				ratingMap[parseInt(ratingRows[rm].user_id, 10)] = parseFloat(ratingRows[rm].rating)
			}
		}

		var ownerRows = Util.queryFast(
			'SELECT user_id, collection_date_owned FROM collection WHERE game_id = ' + gameId + ' AND collection_own = 1'
		)
		var wishRows = Util.queryFast(
			'SELECT user_id FROM collection WHERE game_id = ' + gameId + ' AND collection_wishlist > 0'
		)
		var prevOwnedRows = hasCachedCommunity ? [] : Util.queryFast(
			'SELECT COUNT(DISTINCT user_id) AS c FROM log_user_collection_status WHERE game_id = ' + gameId +
			' AND log_status = ' + Constants.COLLECTION_STATUS_OWN +
			' AND log_context = ' + Constants.LOG_CONTEXT_REMOVED
		)
		var playCountRows = hasCachedCommunity ? [] : Util.queryFast(
			'SELECT user_id, SUM(play_count) AS plays FROM play WHERE game_id = ' + gameId + ' GROUP BY user_id'
		)
		var totalPlayRows = hasCachedCommunity ? [] : Util.queryFast(
			'SELECT SUM(play_count) AS plays, COUNT(DISTINCT user_id) AS players, AVG(play_count) AS avg_plays FROM play WHERE game_id = ' + gameId
		)

		var totalPlayers = latestInsight ? parseInt(latestInsight.insight_game_players, 10) : parseInt((totalPlayRows[0] && totalPlayRows[0].players) || 0, 10)
		var totalPlays = latestInsight ? parseInt(latestInsight.insight_game_total_plays, 10) : parseInt((totalPlayRows[0] && totalPlayRows[0].plays) || 0, 10)
		var avgPlays = latestInsight ? parseFloat(latestInsight.insight_game_avg_plays || 0) : (totalPlayers ? Math.round((totalPlays / totalPlayers) * 10) / 10 : 0)
		var owners = latestInsight ? parseInt(latestInsight.insight_game_owners, 10) : ownerRows.length
		var wishlisted = latestInsight ? parseInt(latestInsight.insight_game_wishlisted, 10) : wishRows.length
		var raters = latestInsight ? parseInt(latestInsight.insight_game_raters, 10) : ratingRows.length
		var avgRating = latestInsight ? parseFloat(latestInsight.insight_game_avg_rating || 0) : (ratingRows.length ? Math.round((ratingRows.reduce(function(sum, row) { return sum + parseFloat(row.rating) }, 0) / ratingRows.length) * 100) / 100 : 0)
		var retention = latestInsight ? parseFloat(latestInsight.insight_game_retention || 0) : 0
		var polarization = (cachedData.polarization !== undefined) ? cachedData.polarization : stddev(ratingRows.map(function(row) { return parseFloat(row.rating) }))
		var prevowners = latestInsight ? parseInt(latestInsight.insight_game_prevowners, 10) : parseInt((prevOwnedRows[0] && prevOwnedRows[0].c) || 0, 10)

		var playCounts = cachedData.playCounts || playCountRows.map(function(row) { return parseInt(row.plays, 10) || 0 })
		var userRatingPercentile = percentileFromBands(ratingDistribution, userRating, raters)
		var userPlayPercentile = (userPlays > 0 && playCounts.length) ? Util.percentile(playCounts.slice().sort(function(a, b) { return a - b }), userPlays) : null

		var majorityRows = hasCachedCommunity ? [] : Util.queryFast(
			'SELECT p.user_id AS user_id, p.play_players AS count, SUM(p.play_count) AS plays FROM play p' +
			' WHERE p.game_id = ' + gameId +
			' AND p.play_players > 0' +
			' GROUP BY p.user_id, p.play_players'
		)

		var userMajority = {}
		var userTotalPlaysMap = {}
		var userPlaysByCount = {}
		var userGridTotals = {}

		for (var mr = 0; mr < majorityRows.length; mr++) {
			var row = majorityRows[mr]
			var uid = parseInt(row.user_id, 10)
			var count = parseInt(row.count, 10)
			var plays = parseInt(row.plays, 10)

			if (!userMajority[uid] || plays > userMajority[uid].plays) userMajority[uid] = { count: count, plays: plays }
			if (!userTotalPlaysMap[uid]) userTotalPlaysMap[uid] = 0
			userTotalPlaysMap[uid] += plays

			if (count <= 6) {
				if (!userPlaysByCount[uid]) userPlaysByCount[uid] = {}
				userPlaysByCount[uid][count] = plays
				if (!userGridTotals[uid]) userGridTotals[uid] = 0
				userGridTotals[uid] += plays
			}
		}

		var countRatings = []
		if (!hasCachedCommunity) {
			var countRatingData = {}
			for (var cr = 0; cr < ratingRows.length; cr++) {
				var ratingRow = ratingRows[cr]
				var ratingUid = parseInt(ratingRow.user_id, 10)
				if (!userMajority[ratingUid]) continue
				var majorityCount = userMajority[ratingUid].count
				if (majorityCount > 10) continue
				if (!countRatingData[majorityCount]) countRatingData[majorityCount] = { sum: 0, n: 0 }
				countRatingData[majorityCount].sum += parseFloat(ratingRow.rating)
				countRatingData[majorityCount].n++
			}
			var countRatingKeys = Object.keys(countRatingData).map(Number).sort(function(a, b) { return a - b })
			for (var ck = 0; ck < countRatingKeys.length; ck++) {
				var countKey = countRatingKeys[ck]
				countRatings.push({
					count: countKey,
					rating: Math.round((countRatingData[countKey].sum / countRatingData[countKey].n) * 10) / 10,
					raters: countRatingData[countKey].n
				})
			}
		}

		var shareRanges = [[85, 101], [70, 85], [55, 70], [40, 55]]
		var countRatingGrid = []
		if (!hasCachedCommunity) {
			var gridData = {}
			var userPlaysByCountKeys = Object.keys(userPlaysByCount)

			for (var ug = 0; ug < userPlaysByCountKeys.length; ug++) {
				var gridUid = parseInt(userPlaysByCountKeys[ug], 10)
				if (!ratingMap[gridUid] || !userGridTotals[gridUid]) continue

				var counts = userPlaysByCount[gridUid]
				var countKeys = Object.keys(counts)
				for (var gc = 0; gc < countKeys.length; gc++) {
					var playerCount = parseInt(countKeys[gc], 10)
					var share = (counts[playerCount] / userGridTotals[gridUid]) * 100
					if (share < 40) continue
					for (var sr = 0; sr < shareRanges.length; sr++) {
						if (share >= shareRanges[sr][0] && share < shareRanges[sr][1]) {
							var gridKey = playerCount + '_' + sr
							if (!gridData[gridKey]) gridData[gridKey] = { sum: 0, n: 0 }
							gridData[gridKey].sum += ratingMap[gridUid]
							gridData[gridKey].n++
							break
						}
					}
				}
			}

			var gridKeys = Object.keys(gridData)
			for (var gk = 0; gk < gridKeys.length; gk++) {
				var parts = gridKeys[gk].split('_')
				var rangeIndex = parseInt(parts[1], 10)
				countRatingGrid.push({
					count: parseInt(parts[0], 10),
					sharePct: shareRanges[rangeIndex][0],
					rating: Math.round((gridData[gridKeys[gk]].sum / gridData[gridKeys[gk]].n) * 10) / 10,
					raters: gridData[gridKeys[gk]].n
				})
			}
		}

		var ownDurationRows = hasCachedCommunity ? [] : Util.queryFast(
			'SELECT user_id, log_context, log_date FROM log_user_collection_status WHERE game_id = ' + gameId +
			' AND log_status = ' + Constants.COLLECTION_STATUS_OWN +
			' ORDER BY user_id, log_date ASC'
		)
		var ownEvents = {}
		for (var od = 0; od < ownDurationRows.length; od++) {
			var ownUid = parseInt(ownDurationRows[od].user_id, 10)
			if (!ownEvents[ownUid]) ownEvents[ownUid] = []
			ownEvents[ownUid].push({
				context: parseInt(ownDurationRows[od].log_context, 10),
				date: parseInt(ownDurationRows[od].log_date, 10)
			})
		}

		var currentOwnerMap = {}
		var ownedDateFallback = {}
		for (var or = 0; or < ownerRows.length; or++) {
			var ownerUid = parseInt(ownerRows[or].user_id, 10)
			currentOwnerMap[ownerUid] = true
			if (parseInt(ownerRows[or].collection_date_owned, 10) > 0) {
				ownedDateFallback[ownerUid] = parseInt(ownerRows[or].collection_date_owned, 10)
			}
		}

		var ownDurations = []
		var userDurations = {}
		var now = Util.time()
		var ownEventUsers = Object.keys(ownEvents)

		for (var ou = 0; ou < ownEventUsers.length; ou++) {
			var eventUid = parseInt(ownEventUsers[ou], 10)
			var totalDays = 0
			var addDate = null
			for (var oe = 0; oe < ownEvents[eventUid].length; oe++) {
				var event = ownEvents[eventUid][oe]
				if (event.context === Constants.LOG_CONTEXT_ADDED) {
					addDate = event.date
				} else if (event.context === Constants.LOG_CONTEXT_REMOVED && addDate !== null) {
					var days = Math.max(1, Math.ceil((event.date - addDate) / Constants.TIME_DAY))
					ownDurations.push(days)
					totalDays += days
					addDate = null
				}
			}
			if (addDate !== null && currentOwnerMap[eventUid]) {
				var currentDays = Math.max(1, Math.ceil((now - addDate) / Constants.TIME_DAY))
				ownDurations.push(currentDays)
				totalDays += currentDays
			}
			if (totalDays > 0) userDurations[eventUid] = totalDays
		}

		if (!ownDurations.length && owners > 0) {
			var fallbackKeys = Object.keys(ownedDateFallback)
			for (var fd = 0; fd < fallbackKeys.length; fd++) {
				var fallbackUid = parseInt(fallbackKeys[fd], 10)
				var fallbackDays = Math.max(1, Math.ceil((now - ownedDateFallback[fallbackUid]) / Constants.TIME_DAY))
				ownDurations.push(fallbackDays)
				userDurations[fallbackUid] = fallbackDays
			}
		}

		var avgTimeOwned = ownDurations.length ? Math.round(ownDurations.reduce(function(sum, value) { return sum + value }, 0) / ownDurations.length) : null
		var durationDistribution = []
		var durationBuckets = [
			{ min: 0, max: 30, label: '< 1 month' },
			{ min: 31, max: 90, label: '1-3 months' },
			{ min: 91, max: 180, label: '3-6 months' },
			{ min: 181, max: 365, label: '6-12 months' },
			{ min: 366, max: 730, label: '1-2 years' },
			{ min: 731, max: 1825, label: '2-5 years' },
			{ min: 1826, max: 3650, label: '5-10 years' },
			{ min: 3651, max: 999999, label: '10+ years' }
		]

		if (ownDurations.length) {
			var bucketCounts = new Array(durationBuckets.length).fill(0)
			for (var bd = 0; bd < ownDurations.length; bd++) {
				for (var bb = 0; bb < durationBuckets.length; bb++) {
					if (ownDurations[bd] >= durationBuckets[bb].min && ownDurations[bd] <= durationBuckets[bb].max) {
						bucketCounts[bb]++
						break
					}
				}
			}
			for (var bi = 0; bi < durationBuckets.length; bi++) {
				durationDistribution.push({ label: durationBuckets[bi].label, count: bucketCounts[bi] })
			}
		}

		var ownershipScatter = []
		var durationKeys = Object.keys(userDurations)
		for (var ds = 0; ds < durationKeys.length; ds++) {
			var scatterUid = parseInt(durationKeys[ds], 10)
			var scatterPlays = userTotalPlaysMap[scatterUid] || 0
			if (!scatterPlays) continue
			ownershipScatter.push({
				days: userDurations[scatterUid],
				plays: scatterPlays,
				rating: ratingMap[scatterUid] || 0,
				id: scatterUid
			})
		}

		var wishDurationRows = hasCachedCommunity ? [] : Util.queryFast(
			'SELECT user_id, log_context, log_date FROM log_user_collection_status WHERE game_id = ' + gameId +
			' AND log_status = ' + Constants.COLLECTION_STATUS_WISHLIST +
			' ORDER BY user_id, log_date ASC'
		)
		var wishEvents = {}
		for (var wd = 0; wd < wishDurationRows.length; wd++) {
			var wishUid = parseInt(wishDurationRows[wd].user_id, 10)
			if (!wishEvents[wishUid]) wishEvents[wishUid] = []
			wishEvents[wishUid].push({
				context: parseInt(wishDurationRows[wd].log_context, 10),
				date: parseInt(wishDurationRows[wd].log_date, 10)
			})
		}

		var currentWishMap = {}
		for (var cw = 0; cw < wishRows.length; cw++) currentWishMap[parseInt(wishRows[cw].user_id, 10)] = true

		var wishDurations = []
		var wishTotal = 0
		var wishConverted = 0
		var wishUserKeys = Object.keys(wishEvents)
		for (var wu = 0; wu < wishUserKeys.length; wu++) {
			var trackedWishUid = parseInt(wishUserKeys[wu], 10)
			var wishAddDate = null
			var wasWishlisted = false
			for (var we = 0; we < wishEvents[trackedWishUid].length; we++) {
				var wishEvent = wishEvents[trackedWishUid][we]
				if (wishEvent.context === Constants.LOG_CONTEXT_ADDED) {
					wishAddDate = wishEvent.date
					wasWishlisted = true
				} else if (wishEvent.context === Constants.LOG_CONTEXT_REMOVED && wishAddDate !== null) {
					wishDurations.push(Math.max(1, Math.ceil((wishEvent.date - wishAddDate) / Constants.TIME_DAY)))
					wishAddDate = null
				}
			}
			if (wishAddDate !== null && currentWishMap[trackedWishUid]) {
				wishDurations.push(Math.max(1, Math.ceil((now - wishAddDate) / Constants.TIME_DAY)))
			}
			if (wasWishlisted) {
				wishTotal++
				if (currentOwnerMap[trackedWishUid]) wishConverted++
			}
		}

		var avgTimeWishlisted = wishDurations.length ? Math.round(wishDurations.reduce(function(sum, value) { return sum + value }, 0) / wishDurations.length) : null
		var wishConvertRate = wishTotal > 0 ? Math.round((wishConverted / wishTotal) * 1000) / 10 : null

		var playTimeRows = hasCachedCommunity ? [] : Util.queryFast(
			'SELECT SUM(play_length * play_count) AS total_minutes, COUNT(*) AS sessions_with_time FROM play WHERE game_id = ' + gameId + ' AND play_length > 0'
		)
		var totalMinutes = parseInt((playTimeRows[0] && playTimeRows[0].total_minutes) || 0, 10)
		var totalHours = totalMinutes > 0 ? Math.round((totalMinutes / 60) * 10) / 10 : null
		var avgDuration = parseInt((playTimeRows[0] && playTimeRows[0].sessions_with_time) || 0, 10) > 0 ? Math.round(totalMinutes / parseInt(playTimeRows[0].sessions_with_time, 10)) : null

		var peerPlayerCounts = cachedData.peerPlayerCounts || {}
		if (!Object.keys(peerPlayerCounts).length) {
			var playerCountRows = Util.queryFast(
				'SELECT play_players AS count, SUM(play_count) AS plays FROM play WHERE game_id = ' + gameId +
				' AND play_players > 0 GROUP BY play_players ORDER BY play_players ASC'
			)
			for (var pc = 0; pc < playerCountRows.length; pc++) {
				peerPlayerCounts[parseInt(playerCountRows[pc].count, 10)] = parseInt(playerCountRows[pc].plays, 10)
			}
		}

		var peerWinRates = cachedData.peerWinRates || {}
		if (!Object.keys(peerWinRates).length) {
			var communityPlayerRows = Util.queryFast(
				'SELECT p.play_players AS count, pl.player_win AS win FROM play p JOIN player pl ON pl.play_id = p.play_id' +
				' WHERE p.game_id = ' + gameId + ' AND p.play_players > 0'
			)
			for (var cp = 0; cp < communityPlayerRows.length; cp++) {
				var countValue = parseInt(communityPlayerRows[cp].count, 10)
				if (!peerWinRates[countValue]) peerWinRates[countValue] = { wins: 0, total: 0 }
				peerWinRates[countValue].total++
				if (parseInt(communityPlayerRows[cp].win, 10)) peerWinRates[countValue].wins++
			}
			var peerWinKeys = Object.keys(peerWinRates)
			for (var pw = 0; pw < peerWinKeys.length; pw++) {
				var winKey = peerWinKeys[pw]
				peerWinRates[winKey] = peerWinRates[winKey].total ? Math.round((peerWinRates[winKey].wins / peerWinRates[winKey].total) * 1000) / 10 : 0
			}
		}

		var userWinRatesByCount = {}
		var playsById = {}
		for (var pbi = 0; pbi < playsRaw.length; pbi++) playsById[parseInt(playsRaw[pbi].play_id, 10)] = playsRaw[pbi]
		for (var ur = 0; ur < playersRaw.length; ur++) {
			var userPlayer = playersRaw[ur]
			if (!this.isSelf(userPlayer, userId, username)) continue
			var refPlay = playsById[parseInt(userPlayer.play_id, 10)]
			if (!refPlay) continue
			var refCount = parseInt(refPlay.play_players, 10)
			if (refCount <= 0) continue
			if (!userWinRatesByCount[refCount]) userWinRatesByCount[refCount] = { wins: 0, total: 0 }
			userWinRatesByCount[refCount].total++
			if (parseInt(userPlayer.player_win, 10)) userWinRatesByCount[refCount].wins++
		}

		var userWinRates = {}
		var uwKeys = Object.keys(userWinRatesByCount)
		for (var uw = 0; uw < uwKeys.length; uw++) {
			var uwCount = uwKeys[uw]
			userWinRates[uwCount] = userWinRatesByCount[uwCount].total
				? Math.round((userWinRatesByCount[uwCount].wins / userWinRatesByCount[uwCount].total) * 1000) / 10
				: 0
		}

		var ratingByPlays = cachedData.ratingByPlays || []
		if (!ratingByPlays.length) {
			var bracketData = {}
			var playCountMap = {}
			for (var pm = 0; pm < playCountRows.length; pm++) playCountMap[parseInt(playCountRows[pm].user_id, 10)] = parseInt(playCountRows[pm].plays, 10)
			var ratingMapKeys = Object.keys(ratingMap)
			for (var rk = 0; rk < ratingMapKeys.length; rk++) {
				var bracketUid = parseInt(ratingMapKeys[rk], 10)
				var bracketCount = playCountMap[bracketUid] || 0
				if (!bracketCount) continue
				var bracket = bucketLabel(bracketCount)
				if (!bracketData[bracket]) bracketData[bracket] = { sum: 0, count: 0 }
				bracketData[bracket].sum += ratingMap[bracketUid]
				bracketData[bracket].count++
			}
			var bracketOrder = ['1', '2', '3', '4-5', '6-7', '8-10', '11-15', '16-20', '21-25', '26-30', '31-40', '41-50', '51-75', '76-100', '101-200', '200+']
			for (var bo = 0; bo < bracketOrder.length; bo++) {
				if (!bracketData[bracketOrder[bo]]) continue
				ratingByPlays.push({
					bracket: bracketOrder[bo],
					rating: Math.round((bracketData[bracketOrder[bo]].sum / bracketData[bracketOrder[bo]].count) * 10) / 10,
					count: bracketData[bracketOrder[bo]].count
				})
			}
		}

		var ratingTrend = cachedData.ratingTrend || []
		if (!ratingTrend.length) {
			var ratingLogRows = Util.queryFast(
				'SELECT log_date, log_rating FROM log_user_rating WHERE game_id = ' + gameId + ' AND log_initial_sync != 1 ORDER BY log_date ASC'
			)
			var trendMap = {}
			for (var tr = 0; tr < ratingLogRows.length; tr++) {
				var trendYear = Util.date('Y', parseInt(ratingLogRows[tr].log_date, 10))
				if (!trendMap[trendYear]) trendMap[trendYear] = { sum: 0, count: 0 }
				trendMap[trendYear].sum += parseFloat(ratingLogRows[tr].log_rating)
				trendMap[trendYear].count++
			}
			var trendKeys = Object.keys(trendMap).sort()
			for (var tk = 0; tk < trendKeys.length; tk++) {
				ratingTrend.push({
					year: trendKeys[tk],
					rating: Math.round((trendMap[trendKeys[tk]].sum / trendMap[trendKeys[tk]].count) * 10) / 10,
					count: trendMap[trendKeys[tk]].count
				})
			}
		}

		var filteredCounts = playCounts.filter(function(value) { return value > 0 }).sort(function(a, b) { return a - b })
		var playHistogram = { position: 0, labels: [], data: [] }
		var playPercentile = null
		var playMedian = null

		if (filteredCounts.length > 1) {
			var maxVal = filteredCounts[filteredCounts.length - 1]
			var minVal = filteredCounts[0]
			if (maxVal > minVal) {
				var range = maxVal - minVal
				if (range <= 20) {
					var valueCounts = {}
					for (var fv = 0; fv < filteredCounts.length; fv++) valueCounts[filteredCounts[fv]] = (valueCounts[filteredCounts[fv]] || 0) + 1
					var position = 0
					for (var val = minVal; val <= maxVal; val++) {
						playHistogram.data.push(valueCounts[val] || 0)
						playHistogram.labels.push(val + ' play' + (val > 1 ? 's' : ''))
						if (userPlays === val) position = playHistogram.data.length - 1
					}
					playHistogram.position = position
				}
				playPercentile = Util.percentile(filteredCounts, userPlays)
				playMedian = userPlays - Util.median(filteredCounts)
			}
		}

		var playBars = { values: [], self: 0, min: 0, bucketWidth: 1, bandSize: 10 }
		if (filteredCounts.length) {
			var rangeMax = 300
			var mainBars = 100
			var bucketWidth = 3
			var barCount = mainBars + 1
			var barValues = new Array(barCount).fill(0)
			for (var fb = 0; fb < filteredCounts.length; fb++) {
				if (filteredCounts[fb] > rangeMax) {
					barValues[barCount - 1]++
				} else {
					barValues[Math.min(mainBars - 1, Math.floor((filteredCounts[fb] - 1) / bucketWidth))]++
				}
			}
			playBars = {
				values: barValues,
				self: userPlays > rangeMax ? (barCount - 1) : Math.min(mainBars - 1, Math.max(0, Math.floor((Math.max(userPlays, 1) - 1) / bucketWidth))),
				min: 1,
				bucketWidth: bucketWidth,
				bandSize: 10,
				overflow: rangeMax + 1
			}
		}

		var tenYearsAgo = Util.time() - (Constants.TIME_YEAR * 10)
		var heatmapPlayRows = hasCachedCommunity ? [] : Util.queryFast(
			'SELECT play_date, play_count FROM play WHERE game_id = ' + gameId + ' AND play_date >= ' + tenYearsAgo
		)
		var communityHeatmap = {}
		var heatmapStart = null
		var heatmapEnd = null
		for (var hm = 0; hm < heatmapPlayRows.length; hm++) {
			var heatDate = parseInt(heatmapPlayRows[hm].play_date, 10)
			var key = weekKey(heatDate)
			if (!communityHeatmap[key]) communityHeatmap[key] = { count: 0 }
			communityHeatmap[key].count += parseInt(heatmapPlayRows[hm].play_count, 10)
			var weekStamp = Util.week_start(heatDate)
			if (heatmapStart === null || weekStamp < heatmapStart) heatmapStart = weekStamp
			if (heatmapEnd === null || weekStamp > heatmapEnd) heatmapEnd = weekStamp
		}
		if (heatmapStart === null) heatmapStart = Util.time()
		if (heatmapEnd === null) heatmapEnd = Util.time()

		var statusCounts = cachedData.statusCounts || null
		if (!statusCounts || (Array.isArray(statusCounts) ? !statusCounts.length : !Object.keys(statusCounts).length)) {
			statusCounts = {}
			statusCounts.own = { id: 'own', count: owners }
			statusCounts.prevowned = { id: 'prevowned', count: prevowners }
			statusCounts.wishlist = { id: 'wishlist', count: wishlisted }
			var otherStatusRows = Util.queryFast(
				'SELECT' +
				' SUM(CASE WHEN collection_fortrade = 1 THEN 1 ELSE 0 END) AS fortrade,' +
				' SUM(CASE WHEN collection_want = 1 THEN 1 ELSE 0 END) AS want,' +
				' SUM(CASE WHEN collection_wanttoplay = 1 THEN 1 ELSE 0 END) AS wanttoplay,' +
				' SUM(CASE WHEN collection_wanttobuy = 1 THEN 1 ELSE 0 END) AS wanttobuy,' +
				' SUM(CASE WHEN collection_preordered = 1 THEN 1 ELSE 0 END) AS preordered' +
				' FROM collection WHERE game_id = ' + gameId
			)
			var statusRow = otherStatusRows[0] || {}
			statusCounts.fortrade = { id: 'fortrade', count: parseInt(statusRow.fortrade || 0, 10) }
			statusCounts.want = { id: 'want', count: parseInt(statusRow.want || 0, 10) }
			statusCounts.wanttoplay = { id: 'wanttoplay', count: parseInt(statusRow.wanttoplay || 0, 10) }
			statusCounts.wanttobuy = { id: 'wanttobuy', count: parseInt(statusRow.wanttobuy || 0, 10) }
			statusCounts.preordered = { id: 'preordered', count: parseInt(statusRow.preordered || 0, 10) }
		}

		var retentionThreshold = Util.time() - Constants.TIME_YEAR
		var userRetained = false
		for (var up = 0; up < playsRaw.length; up++) {
			if (parseInt(playsRaw[up].play_date, 10) >= retentionThreshold) {
				userRetained = true
				break
			}
		}

		return {
			peers: {
				statusCounts: statusCounts,
				owners: owners,
				prevowners: prevowners,
				wishlisted: wishlisted,
				avgTimeOwned: avgTimeOwned,
				avgTimeWishlisted: avgTimeWishlisted,
				wishConvertRate: wishConvertRate,
				totalHours: totalHours,
				avgDuration: avgDuration,
				raters: raters,
				avgRating: avgRating,
				totalPlayers: totalPlayers,
				totalPlays: totalPlays,
				avgPlays: avgPlays,
				polarization: polarization,
				ratingDistribution: ratingDistribution,
				userRating: userRating,
				userRatingPercentile: userRatingPercentile,
				playDistribution: cachedData.playDistribution || [],
				playHistogram: playHistogram,
				playBars: playBars,
				playPercentile: playPercentile,
				playMedian: playMedian,
				userPlays: userPlays,
				userPlayPercentile: userPlayPercentile,
				userPlaysPercentile: userPlayPercentile,
				peerPlayerCounts: peerPlayerCounts,
				peerWinRates: peerWinRates,
				userWinRates: userWinRates,
				ratingByPlays: ratingByPlays,
				countRatings: countRatings,
				countRatingGrid: countRatingGrid,
				durationDistribution: durationDistribution,
				ownershipScatter: ownershipScatter,
				retention: retention,
				userRetained: userRetained,
				ratingTrend: ratingTrend,
				history: history,
				communityHeatmap: {
					plays: communityHeatmap,
					start: heatmapStart,
					end: heatmapEnd,
					total: Object.keys(communityHeatmap).length
				}
			}
		}

	}
})
